import i18n from '@/utils/i18n';
import React from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { Button } from '@/components/button';
import { PadAdditionalTerms } from '@/components/payments/pad-additional-terms';
import { PadAgreement } from '@/components/payments/pad-agreement';
import { PadContactInformationInputs } from '@/components/payments/pad-contact-information-inputs';
import { PadPaymentInfoInputs } from '@/components/payments/pad-payment-info-inputs';
import { StyleSheet, View } from 'react-native';
import { usePadPaymentFlow } from '@/hooks/payments/use-pad-payment-operations';

export enum PadPaymentFormView {
  AdditionalTerms = 'ADDITIONAL_TERMS',
  Agreement = 'AGREEMENT',
  ContactInformation = 'CONTACT_INFORMATION',
  PaymentInfo = 'PAYMENT_INFO',
}

export interface PadPaymentFormProps {
  currentView: PadPaymentFormView;
  isCollectingConsent?: boolean;
  onChangeView: (view: PadPaymentFormView) => void;
  onClose: () => void;
}

export const PadPaymentForm: React.FC<PadPaymentFormProps> = ({
  currentView,
  isCollectingConsent,
  onChangeView,
  onClose,
}) => {
  const {
    formState,
    handleSubmitPad,
    dispatch,
    isLoadingConsentForms,
    isSubmittingPad,
    consentFormsData,
    fetchConsentAgreementText,
    collectConsentForPaymentMethod,
  } = usePadPaymentFlow();

  const onSubmit = async () => {
    if (isCollectingConsent) {
      await collectConsentForPaymentMethod(onClose);
    } else {
      await handleSubmitPad(onClose);
    }
  };

  switch (currentView) {
    case PadPaymentFormView.ContactInformation: {
      const { contactInfo, padBusinessType } = formState;
      const isContactInfoFormValid = Boolean(
        contactInfo.payeeName.trim() &&
          contactInfo.email.trim() &&
          contactInfo.phoneNumber.trim() &&
          contactInfo.street.trim() &&
          contactInfo.city.trim() &&
          contactInfo.province.trim() &&
          contactInfo.postalCode.trim() &&
          (!isCollectingConsent || padBusinessType),
      );
      return (
        <View style={styles.flex}>
          <PadContactInformationInputs
            dispatch={dispatch}
            formState={formState}
            isCollectingConsent={isCollectingConsent}
          />
          <Button
            disabled={!isContactInfoFormValid}
            onPress={() => {
              if (isCollectingConsent) {
                fetchConsentAgreementText();
                onChangeView(PadPaymentFormView.Agreement);
              } else {
                onChangeView(PadPaymentFormView.PaymentInfo);
              }
            }}
            style={styles.continueButton}
            trackingData={{ name: 'pad_contact_info.continue_button' }}
          >
            {i18n.t('continue')}
          </Button>
        </View>
      );
    }
    case PadPaymentFormView.PaymentInfo: {
      const { paymentMethod, padBusinessType } = formState;
      const isPaymentInfoValid = Boolean(
        paymentMethod.ownerName.trim() &&
          paymentMethod.bankCode.trim() &&
          paymentMethod.bankLocationId.trim() &&
          paymentMethod.bankAccountNumber.trim() &&
          padBusinessType,
      );

      return (
        <View style={styles.flex}>
          <PadPaymentInfoInputs dispatch={dispatch} formState={formState} />
          <Button
            disabled={!isPaymentInfoValid}
            onPress={async () => {
              onChangeView(PadPaymentFormView.Agreement);
              await fetchConsentAgreementText();
            }}
            style={styles.continueButton}
            trackingData={{ name: 'pad_payment_info.continue_button' }}
          >
            {i18n.t('continue')}
          </Button>
        </View>
      );
    }
    case PadPaymentFormView.Agreement: {
      if (isLoadingConsentForms || !consentFormsData) {
        return (
          <View style={styles.loadingContainer}>
            <ActivityIndicator />
          </View>
        );
      }

      return (
        <View style={styles.flex}>
          <PadAgreement
            markdownTemplate={consentFormsData.padAgreement}
            onViewAdditionalTerms={() =>
              onChangeView(PadPaymentFormView.AdditionalTerms)
            }
          />
          <Button
            loading={isSubmittingPad}
            onPress={onSubmit}
            style={styles.continueButton}
            trackingData={{ name: 'pad_agreement.agree_button' }}
          >
            {i18n.t('agree')}
          </Button>
        </View>
      );
    }
    case PadPaymentFormView.AdditionalTerms: {
      if (!consentFormsData) {
        return (
          <View style={styles.loadingContainer}>
            <ActivityIndicator />
          </View>
        );
      }

      return (
        <PadAdditionalTerms
          markdownTemplate={consentFormsData.padAdditionalTerms}
        />
      );
    }
    default: {
      return null;
    }
  }
};

const styles = StyleSheet.create({
  continueButton: {
    borderRadius: 8,
    marginHorizontal: 16,
    marginTop: 8,
  },
  flex: {
    flex: 1,
  },
  loadingContainer: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
});
